import { useEffect, useState } from 'react';
import { InfoCard, Link } from '@backstage/core-components';
import { useApi } from '@backstage/core-plugin-api';
import { catalogApiRef } from '@backstage/plugin-catalog-react';
import {
  Chip,
  CircularProgress,
  List,
  ListItem,
  ListItemText,
  Typography,
} from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import type { Entity } from '@backstage/catalog-model';

const useStyles = makeStyles(theme => ({
  loading: {
    display: 'flex',
    justifyContent: 'center',
    padding: theme.spacing(3),
  },
  empty: {
    padding: theme.spacing(2),
    color: theme.palette.text.secondary,
    textAlign: 'center',
  },
  chip: {
    marginLeft: theme.spacing(1),
    height: 18,
    fontSize: '0.65rem',
  },
  listItem: {
    borderRadius: theme.shape.borderRadius,
    '&:hover': { backgroundColor: theme.palette.action.hover },
  },
}));

const kindColor = (kind: string): 'default' | 'primary' | 'secondary' => {
  if (kind === 'Component') return 'primary';
  if (kind === 'API') return 'secondary';
  return 'default';
};

export const MyServicesCard = ({ userEntityRef }: { userEntityRef?: string }) => {
  const classes = useStyles();
  const catalogApi = useApi(catalogApiRef);
  const [entities, setEntities] = useState<Entity[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string>();

  useEffect(() => {
    if (!userEntityRef) return;
    setLoading(true);
    setError(undefined);
    catalogApi
      .getEntities({
        filter: { 'relations.ownedBy': userEntityRef },
        fields: ['kind', 'metadata.name', 'metadata.namespace', 'spec.type'],
      })
      .then((resp: { items: Entity[] }) => setEntities(resp.items))
      .catch(() => setError('Could not load services'))
      .finally(() => setLoading(false));
  }, [catalogApi, userEntityRef]);

  const content = () => {
    if (!userEntityRef || loading) {
      return (
        <div className={classes.loading}>
          <CircularProgress size={24} />
        </div>
      );
    }
    if (error) {
      return <Typography className={classes.empty}>{error}</Typography>;
    }
    if (entities.length === 0) {
      return (
        <Typography className={classes.empty}>
          You don't own any catalog entries yet.{' '}
          <Link to="/create">Run a template</Link> or{' '}
          <Link to="/catalog-import">import a repo</Link> to get started.
        </Typography>
      );
    }
    return (
      <List dense disablePadding>
        {entities.slice(0, 8).map(e => {
          const ns = e.metadata.namespace ?? 'default';
          const href = `/catalog/${ns}/${e.kind.toLowerCase()}/${e.metadata.name}`;
          return (
            <ListItem key={`${e.kind}:${ns}/${e.metadata.name}`} button component="a" href={href} className={classes.listItem}>
              <ListItemText
                primary={e.metadata.name}
                secondary={(e.spec?.type as string) ?? e.kind}
              />
              <Chip
                label={e.kind}
                size="small"
                color={kindColor(e.kind)}
                className={classes.chip}
              />
            </ListItem>
          );
        })}
        {entities.length > 8 && (
          <Typography variant="caption" style={{ padding: 8, display: 'block' }}>
            +{entities.length - 8} more — <Link to="/catalog?filters[owned]=owned">view all</Link>
          </Typography>
        )}
      </List>
    );
  };

  return (
    <InfoCard
      title="My Services"
      subheader={entities.length > 0 ? `${entities.length} owned ${entities.length === 1 ? 'entry' : 'entries'}` : undefined}
      action={<Link to="/catalog?filters[owned]=owned">View all</Link>}
    >
      {content()}
    </InfoCard>
  );
};
